/**
 * @fileoverview A component for uploading and managing media (images and videos) for a post.
 * It supports multiple file uploads and allows the user to select one for the post.
 */
import React, { useRef, useState, useEffect } from 'react';
import { UploadIcon, TrashIcon, PlusCircleIcon } from './icons';
import { MediaType } from '../types';
import { Tooltip } from './Tooltip';

interface MediaUploaderProps {
  mediaPreview: string | null; // The base64 URL of the currently selected media.
  mediaType: MediaType | null; // The type of the selected media.
  onMediaChange: (base64: string | null, type: MediaType | null, description?: string) => void; // Callback to update the parent component.
  hasError?: boolean; // Indicates if there is a validation error.
}

// Represents a single uploaded media item.
interface UploadedMedia {
  url: string; // base64 data URL
  type: MediaType;
  description?: string;
}

/**
 * Helper function to read a file as a base64 Data URL.
 * @param file - The file to read.
 * @returns A promise that resolves with the media URL and type, or null if the file type is invalid.
 */
const readFileAsDataURL = (file: File): Promise<UploadedMedia | null> => {
  return new Promise((resolve) => {
    let type: MediaType | null = null;
    let description: string | undefined;
    if (file.type.startsWith('image/')) {
      type = 'image';
    } else if (file.type.startsWith('video/')) {
      type = 'video';
      description = 'Video content';
    } else {
      resolve(null); // Invalid file type
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      resolve({ url: reader.result as string, type: type as MediaType, description });
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
};


export const MediaUploader: React.FC<MediaUploaderProps> = ({ mediaPreview, onMediaChange, hasError }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // State to store all media items uploaded in the current session (before scheduling).
  const [uploadedMedia, setUploadedMedia] = useState<UploadedMedia[]>([]);

  // Effect to clear the internal state when the parent form is reset (mediaPreview becomes null).
  useEffect(() => {
    if (mediaPreview === null && uploadedMedia.length > 0) {
      setUploadedMedia([]);
    }
  }, [mediaPreview, uploadedMedia.length]);


  // Handles file selection from the file input.
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newMediaPromises = Array.from(files).map(readFileAsDataURL);
      const newMediaItems = (await Promise.all(newMediaPromises)).filter(Boolean) as UploadedMedia[];

      if (newMediaItems.length > 0) {
        const updatedMedia = [...uploadedMedia, ...newMediaItems];
        setUploadedMedia(updatedMedia);

        // If no media was selected before, automatically select the first new item.
        if (!mediaPreview) {
          onMediaChange(newMediaItems[0].url, newMediaItems[0].type, newMediaItems[0].description);
        }
      }
    }
     // Reset file input value to allow re-uploading the same file if needed.
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  };

  // Sets the selected media for the post when a thumbnail is clicked.
  const handleSelectMedia = (media: UploadedMedia) => {
    onMediaChange(media.url, media.type, media.description);
  };

  // Removes a media item from the uploader.
  const handleRemoveMedia = (e: React.MouseEvent, urlToRemove: string) => {
    e.stopPropagation(); // Prevent the click from selecting the media.
    const newUploadedMedia = uploadedMedia.filter(m => m.url !== urlToRemove);
    setUploadedMedia(newUploadedMedia);

    // If the removed media was the one currently selected, update the selection.
    if (mediaPreview === urlToRemove) {
      if (newUploadedMedia.length > 0) {
        // Select the first remaining media item.
        onMediaChange(newUploadedMedia[0].url, newUploadedMedia[0].type, newUploadedMedia[0].description);
      } else {
        // No media left, so clear the selection.
        onMediaChange(null, null);
      }
    }
  };

  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept="image/png, image/jpeg, image/gif, video/mp4, video/quicktime, video/webm"
        multiple // Allow selecting multiple files at once.
      />
      {uploadedMedia.length === 0 ? (
        // Initial state: Big upload button.
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className={`w-full flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 hover:border-slate-400 dark:hover:border-gray-500 transition ${
            hasError ? 'border-red-500' : 'border-slate-300 dark:border-gray-600'
          }`}
        >
          <UploadIcon className="w-8 h-8 mb-2" />
          <span>Click to upload Image/Video</span>
          <span className="text-xs">PNG, JPG, GIF, MP4, MOV</span>
        </button>
      ) : (
        // State after upload: Grid of media thumbnails.
        <div className="space-y-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Select a media item for your post:</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {uploadedMedia.map((media) => (
              <div
                key={media.url.slice(-50)} // Using a slice for a more stable key on re-renders.
                onClick={() => handleSelectMedia(media)}
                className={`relative aspect-square rounded-lg overflow-hidden group bg-slate-200 dark:bg-gray-900 flex items-center justify-center cursor-pointer transition-all ${
                  mediaPreview === media.url ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-blue-500 dark:ring-blue-400' : 'ring-0'
                }`}
              >
                {media.type === 'image' && <img src={media.url} alt="Preview" className="w-full h-full object-cover" />}
                {media.type === 'video' && <video src={media.url} className="w-full h-full object-cover" muted loop playsInline autoPlay />}
                
                {/* Remove button overlay on hover */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-300 flex items-center justify-center">
                  <Tooltip text="Remove media">
                    <button
                      onClick={(e) => handleRemoveMedia(e, media.url)}
                      className="bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300 transform hover:bg-red-700 hover:scale-110"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ))}
            {/* Button to add more media */}
             <Tooltip text="Add more media">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square flex flex-col items-center justify-center border-2 border-dashed rounded-lg text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-700 hover:border-slate-400 dark:hover:border-gray-500 transition border-slate-300 dark:border-gray-600"
                >
                  <PlusCircleIcon className="w-8 h-8" />
                </button>
              </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};
